import { useQuery } from '@tanstack/react-query'
import { Users } from 'lucide-react'
import { listPersonalLoans, type PersonalLoan } from '@/api/personalLoans'
import { EmptyState } from '@/components/ui/empty-state'
import { useListAmountFormat } from '@/hooks/useFormatCurrency'
import { LoansHero } from '@/components/loans/LoansHero'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { PersonalLoanRow } from './PersonalLoanRow'

function Group({ label, total, loans }: { label: string; total: string; loans: PersonalLoan[] }) {
  if (loans.length === 0) return null
  return (
    <section
      aria-label={label}
      className="overflow-hidden rounded-[14px] border border-[var(--fl-border)] bg-[var(--fl-surface)]"
    >
      <div className="flex items-baseline justify-between gap-2 border-b border-[var(--fl-divider)] px-4 py-3">
        <h3 className="text-sm font-semibold text-[var(--fl-heading)]">{label}</h3>
        <span className="text-xs text-[var(--fl-text-3)]">{total}</span>
      </div>
      <ul className="divide-y divide-[var(--fl-divider)]">
        {loans.map((l) => (
          <PersonalLoanRow key={l.accountId} loan={l} title={l.contact.name} avatar />
        ))}
      </ul>
    </section>
  )
}

const sum = (loans: PersonalLoan[], pick: (l: PersonalLoan) => number) =>
  loans.reduce((total, l) => total + pick(l), 0)

/** Loans page "With people" tab: money lent to / borrowed from people as loans. */
export function PersonalLoansTab() {
  const format = useListAmountFormat()
  const { data: loans = [], isLoading } = useQuery({
    queryKey: ['personal-loans', 'all'],
    queryFn: () => listPersonalLoans(),
  })

  if (isLoading) return <p className="text-sm text-[var(--fl-text-2)]">Loading…</p>

  if (loans.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No loans with people"
        description="Lent money to a friend, or borrowed from family? Track it here, with repayments and an optional due date."
      />
    )
  }

  const lent = loans.filter((l) => l.direction === 'LENT')
  const borrowed = loans.filter((l) => l.direction === 'BORROWED')
  const owedToYou = sum(lent, (l) => l.outstanding)
  const youOwe = sum(borrowed, (l) => l.outstanding)
  const net = fromPaise(toPaise(owedToYou) - toPaise(youOwe))
  const open = (ls: PersonalLoan[]) => ls.filter((l) => !l.closed).length

  return (
    <div className="flex flex-col gap-3 md:gap-5">
      <LoansHero
        ariaLabel="Loans with people summary"
        label={net >= 0 ? 'Net, people owe you' : 'Net, you owe people'}
        amount={Math.abs(net)}
        tone={net < 0 ? 'expense' : undefined}
        split={{ good: owedToYou, bad: youOwe }}
        items={[
          {
            label: 'People owe you',
            amount: owedToYou,
            tone: 'income',
            sub: `${open(lent)} open ${open(lent) === 1 ? 'loan' : 'loans'}`,
          },
          {
            label: 'You owe people',
            amount: youOwe,
            tone: 'expense',
            sub: `${open(borrowed)} open ${open(borrowed) === 1 ? 'loan' : 'loans'}`,
          },
          {
            label: 'Interest received',
            amount: sum(lent, (l) => l.interestPaid),
            sub: 'from people, all time',
          },
        ]}
      />
      <div className="grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-2">
        <Group label="Lent" total={`${format(owedToYou)} owed to you`} loans={lent} />
        <Group label="Borrowed" total={`${format(youOwe)} you owe`} loans={borrowed} />
      </div>
      <p className="text-[12.5px] text-[var(--fl-text-2)]">
        For quick money lent or borrowed without repayment tracking, use Lend / Borrow in Add
        transaction. Those show up under People.
      </p>
    </div>
  )
}
